import { useEffect, useMemo, useState } from "react";

import { withTranslation } from "react-i18next";
import { Box, Divider, Typography } from "@mui/material";
import { useDispatch } from "react-redux";

import { cloneDeep, isEmpty, isEqual, max, size } from "lodash";

import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import ColorUtil from "@plannel/pages/utils/color-util";
import codeUtil from "@plannel/pages/utils/code-util";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import AppCard, { CARD_HEADER_SX } from "@plannel/components/ui/AppCard";
import StyledSwitch from "@plannel/components/ui/StyledSwitch";
import PositionHierarchyBoard from "@plannel/components/access-control/PositionHierarchyBoard";
import PositionRulePanel from "@plannel/components/access-control/PositionRulePanel";
import RuleMemberList from "@plannel/components/access-control/RuleMemberList";

import hrchyAclService from "@plannel/services/data/hrchy-acl-service";
import measureAclService from "@plannel/services/data/measure-acl-service";
import postnService from "@plannel/services/data/postn-service";
import customerHrchyService from "@plannel/services/data/customer-hrchy-service";
import customerItemService from "@plannel/services/data/customer-item-service";
import planConfigService from "@plannel/services/data/plan-config-service";
import userService from "@plannel/services/system/user-service";
import itemHrchyService from "@plannel/services/data/item-hrchy-service";
import { FS } from "@plannel/utils/font-util";

/*
  직책 접근 제어 — 계층 보드(좌) · 규칙 패널(우) · 선택 규칙의 구성원(좌하) 3구획.

  이전 화면은 "전 직책의 ACL 평면 그리드" + "ReactFlow 조직도" 조합이라,
  직책 하나의 권한을 확인하려면 조직도에서 노드를 클릭해 그리드를 서버 재조회로 필터링해야 했다.
  지금은 ACL 을 한 번에 다 받아 클라이언트에서 직책별로 묶는다. 그래서
    · 계층 보드의 카드마다 "규칙 N개"를 바로 띄울 수 있고(어디에 권한이 몰려 있는지 한눈에),
    · 카드 클릭이 서버 왕복 없이 즉시 반응한다.
  그리드 편집(DataState) 대신 로컬 상태로 dirty 를 추적한다 — 규칙이 카드 UI 로 바뀌었기 때문.
*/
const newRuleKey = (() => {
  let seq = 0;
  return () => `new-${++seq}`;
})();

const AccessControlAdmin = ({ t, viewName, title }) => {
  const reduxDispatch = useDispatch();

  const userData = JSON.parse(localStorage.getItem("user"));
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const [isLoading, setIsLoading] = useState(false);
  const [userPostn, setUserPostn] = useState([]);
  const [postnData, setPostnData] = useState([]);
  const [postnClsrMap, setPostnClsrMap] = useState({});
  const [customerHrchyData, setCustomerHrchyData] = useState([]);
  const [itemHrchyData, setItemHrchyData] = useState([]);
  const { dpMeasure, dpcommonMeasureCd, dpAccessControlTab, dpConfig: { APPROVAL_ACCESS_CONTROL, ACCESS_CONTROL } } = codeUtil;
  const [customerData, setCustomerData] = useState([]);
  const [itemData, setItemData] = useState([]);

  const [hrchyRules, setHrchyRules] = useState([]);
  const [measureRules, setMeasureRules] = useState([]);
  const [selectedPostnCd, setSelectedPostnCd] = useState(null);
  const [selectedRuleKey, setSelectedRuleKey] = useState({ [dpAccessControlTab.CUSTHRCHY]: null, [dpAccessControlTab.MEASURE]: null });
  const [tab, setTab] = useState(dpAccessControlTab.CUSTHRCHY);

  const [accessControl, setAccessControl] = useState([]);
  const [approvalAccessControl, setApprovalAccessControl] = useState([]);
  const [toggled, setToggled] = useState(false);
  const [approvalToggled, setApprovalToggled] = useState(false);
  const [apvlTarget, setApvlTarget] = useState(null);

  const [fullScreen, setFullScreen] = useState(false);
  const { snackBarMessageColor } = ColorUtil;

  const isMeasureTab = tab === dpAccessControlTab.MEASURE;
  const rules = isMeasureTab ? measureRules : hrchyRules;
  const setRules = isMeasureTab ? setMeasureRules : setHrchyRules;

  const topCustHrchy = useMemo(() => customerHrchyData.find(hrchy => hrchy?.parentHrchy === null), [customerHrchyData]);
  const topItemHrchy = useMemo(() => itemHrchyData.find(hrchy => hrchy?.parentHrchy === null), [itemHrchyData]);

  const postnByCd = useMemo(() => {
    const map = {};
    for (const postn of postnData) {
      map[postn.postnCd] = postn;
    }
    return map;
  }, [postnData]);

  const selectedPostn = selectedPostnCd ? postnByCd[selectedPostnCd] : null;

  const selectedPostnPath = useMemo(() => {
    const path = [];
    let cursor = selectedPostn;
    const guard = new Set();
    while (cursor && !guard.has(cursor.postnCd)) {
      guard.add(cursor.postnCd);
      path.unshift(cursor.name);
      cursor = postnByCd[cursor?.parentPostn?.postnCd];
    }
    return path;
  }, [selectedPostn, postnByCd]);

  const visibleRules = useMemo(
    () => rules.filter(rule => rule.postnCd === selectedPostnCd),
    [rules, selectedPostnCd]
  );

  const ruleCountByPostn = useMemo(() => {
    const counts = {};
    for (const rule of rules) {
      counts[rule.postnCd] = (counts[rule.postnCd] ?? 0) + 1;
    }
    return counts;
  }, [rules]);

  const dirtyCount = useMemo(() => rules.filter(rule => rule._dirty).length, [rules]);

  const activeRuleKey = selectedRuleKey[tab];
  const activeRule = visibleRules.find(rule => rule._key === activeRuleKey) ?? null;

  // ── 조회 ────────────────────────────────────────────────────────────────────
  const retrieveAccessControl = () => {
    planConfigService
      .getAll({ moduleCd: "DP", activeFlg: true })
      .then(response => {
        if (response.data) {
          for (const result of response.data.results) {
            if (result.configCd === ACCESS_CONTROL) {
              setAccessControl(result);
            } else if (result.configCd === APPROVAL_ACCESS_CONTROL) {
              setApprovalAccessControl(result);
            }
          }
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrievePostn = () => {
    postnService.getAll()
      .then(response => {
        const { results } = response.data;
        const postnSort = [];
        results.sort((a, b) => {
          return a.postnPath.length - b.postnPath.length;
        });
        const pathlLengths = results.map(postn => postn?.postnPath?.length);
        const maxPath = max(pathlLengths);

        for (let length = 1; length < maxPath + 1; length++) {
          const filteredList = results.filter(postn => postn.postnPath.length === length);
          let cnt = 1;
          let parentCd = "";

          for (const filterPostn of filteredList) {
            if (!filterPostn.activeFlg) {
              continue;
            }
            const existParent = postnSort.filter(sort => isEqual(sort.cd, filterPostn?.parentPostn?.postnCd));
            if (size(existParent) > 0) {
              if (!isEqual(parentCd, existParent[0]?.cd)) {
                parentCd = existParent[0]?.cd;
                cnt = 1;
              }
              const parentSortCnt = existParent?.[0]?.sort ?? 1;
              const sort = `${parentSortCnt}_${cnt}`;
              postnSort.push({ cd: filterPostn.postnCd, sort, level: length });

            } else {
              postnSort.push({ cd: filterPostn.postnCd, sort: cnt, level: length });
            }
            cnt++;
          }
        }

        postnSort.sort((a, b) => {
          return a.sort - b.sort;
        });

        const sortedPostn = []
        for (const row of postnSort) {
          const postn = { ...row, ...results.filter(item => item.postnCd === row.cd)[0] }
          sortedPostn.push(postn)
        }
        setPostnData(sortedPostn);
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrievePostnClsr = () => {
    postnService.getClrs()
      .then(response => {
        const clsrMap = {};
        for (const hrchy of response.data) {
          let list = clsrMap[hrchy.ancesterCd];
          if (!list) {
            list = [];
          }
          list.push(hrchy.descendantCd);
          clsrMap[hrchy.ancesterCd] = list;
        }
        setPostnClsrMap(clsrMap);
      })
      .catch(e => {
        debug(e);
      });
  }

  const getLeafHrchyCds = (hrchyCd, hrchyData) => {
    if (!hrchyCd || !hrchyData) {
      return [];
    }
    return hrchyData.filter(hrchy => hrchy.hrchyPath?.includes(hrchyCd) && hrchy.leafFlg).map(hrchy => hrchy.hrchyCd);
  }

  const retrieveCustomerHrchy = () => {
    customerHrchyService.getAll({ searchFilters: { hrchyNum: codeUtil.hrchyNum.HRCHY, isAll: true } })
      .then(response => {
        setCustomerHrchyData(response?.data?.results ?? []);
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveItemHrchy = () => {
    itemHrchyService.getAll({ searchFilters: { hrchyNum: codeUtil.hrchyNum.HRCHY, isAll: true } })
      .then(response => {
        setItemHrchyData(response?.data?.results ?? []);
      })
      .catch(e => {
        debug(e);
      });
  }

  const setGridData = (datas) => {
    const gridData = [];
    if (datas) {
      for (const row of datas) {
        gridData.push({
          itemCd: row.itemCd,
          itemNm: row.itemName,
          customerCd: row.customerCd,
          customerNm: row.customerName,
          customerHrchyCd: row.custHrchyCd,
          customerHrchyName: row.custHrchyName,
          itemHrchyCd: row.itemHrchyCd,
          itemHrchyNm: row.itemHrchyName,
        });
      }
    }
    setItemData(Array.from(new Map(gridData.map(row => [row.itemCd, { ...row }])).values()));
    setCustomerData(Array.from(new Map(gridData.map(row => [row.customerCd, { ...row }])).values()));
  }

  const dispatchViewState = () => {
    const reduxState = reduxUtil.createViewState(viewName);
    reduxDispatch(reduxUtil.updateViewState(reduxState));
  }

  const retrieveCustItemByHrchyCd = (params) => {
    const { custHrchyCds, itemHrchyCds } = params;
    customerItemService.getByHrchyCds({ leafCustHrchyCd: custHrchyCds, leafItemHrchyCds: itemHrchyCds })
      .then(response => {
        setGridData(response.data.results);
      })
      .catch(e => {
        debug(e);
      })
      .then(dispatchViewState);
  }

  const retrieveCustItemByPostnCd = (params) => {
    customerItemService.getByPostn({ postnCds: params })
      .then(response => {
        setGridData(response.data.results);
      })
      .catch(e => {
        debug(e);
      })
      .then(dispatchViewState);
  }

  const retrieveUserInfo = () => {
    const postnCodeList = [];
    userService.getUser({ username: userData.username })
      .then(response => {
        const userInfo = response.data.results[0];
        for (const row of userInfo.postn) {
          postnCodeList.push(row.postnCd)
        }
        setUserPostn(postnCodeList)
        retrieveCustItemByPostnCd(postnCodeList);
      })
      .catch(e => { debug(e); });
  }

  const splitPermission = (permsn) => {
    if (!permsn) {
      return { ownerPermission: "0", groupPermission: "0", othersPermission: "0" };
    }
    const text = permsn.toString();
    return {
      ownerPermission: text.substring(0, 1),
      groupPermission: text.substring(1, 2),
      othersPermission: text.substring(2, 3),
    };
  }

  const retrieveHrchyAcl = () => {
    hrchyAclService.getAll({ searchFilters: {} })
      .then(response => {
        const result = response.data.results.map(row => ({
          _key: String(row.id),
          _dirty: false,
          id: row.id,
          postnId: row.postn.id,
          customerHrchyCd: row.customerHrchy?.hrchyCd ?? topCustHrchy?.hrchyCd,
          customerHrchyName: row.customerHrchy?.name ?? topCustHrchy?.name,
          itemHrchyCd: row?.itemHrchy?.hrchyCd ?? topItemHrchy?.hrchyCd,
          itemHrchyNm: row?.itemHrchy?.name ?? topItemHrchy?.name,
          postnCd: row.postn.postnCd,
          postnName: row.postn.name,
          nodePermsn: row.nodePermsn,
          ...splitPermission(row.nodePermsn),
          customerHrchy: row.customerHrchy,
          itemHrchy: row.itemHrchy,
          postn: row.postn,
          users: row.users,
          verNum: row.verNum,
          createdTs: row.createdTs,
          updatedTs: row.updatedTs,
        }));
        setHrchyRules(result);
      })
      .catch(e => {
        debug(e);
      })
      .then(dispatchViewState);
  }

  const retrieveMeasureAcl = () => {
    measureAclService.getAll({ searchFilters: {} })
      .then(response => {
        const result = [];
        for (const row of response.data.results) {
          const measureCmCd = dpMeasure[row.measureCd]?.common;
          if (!row.measureCd.includes("AMT") && dpcommonMeasureCd[measureCmCd] !== null) {
            result.push({
              _key: String(row.id),
              _dirty: false,
              id: row.id,
              postnId: row.postn.id,
              measureCommonCd: measureCmCd,
              measureCd: row.measureCd,
              measureName: dpcommonMeasureCd[measureCmCd]?.name,
              postnCd: row.postn.postnCd,
              postnName: row.postn.name,
              measurePermsn: row.measurePermsn,
              ...splitPermission(row.measurePermsn),
              postn: row.postn,
              verNum: row.verNum,
              createdTs: row.createdTs,
              updatedTs: row.updatedTs,
            });
          }
        }
        // QTY 행 하나로 편집하고 저장 때 AMT 짝을 함께 밀어 넣는다(측정치는 금액/수량이 한 쌍).
        for (const aclData of result) {
          for (const row of response.data.results) {
            if (isEqual(dpMeasure[row.measureCd].common, aclData.measureCommonCd)
              && isEqual(row.postn.postnCd, aclData.postnCd)
              && row.measureCd.includes("AMT")) {
              aclData.pairRow = row;
            }
          }
        }
        setMeasureRules(result);
      })
      .catch(e => {
        debug(e);
      })
      .then(dispatchViewState);
  }

  // ── 규칙 편집 ──────────────────────────────────────────────────────────────
  const customerHrchyOptions = useMemo(
    () => customerHrchyData.map(hrchy => ({ key: hrchy.hrchyCd, label: hrchy.name })),
    [customerHrchyData]
  );
  const itemHrchyOptions = useMemo(
    () => itemHrchyData.map(hrchy => ({ key: hrchy.hrchyCd, label: hrchy.name })),
    [itemHrchyData]
  );
  /*
    지표 이름은 t() 로 풀면 안 된다. utils/i18n.js 가 언어팩의 grid 섹션만 별도 네임스페이스로 두고
    나머지(menu/msg/…)를 default 로 합치는데, 지표 이름은 두 섹션에 갈라져 있다 —
    marketingForecast 는 grid 에만, salesPlan 은 menu 에만 있다.
    그래서 t() 는 marketingForecast 를, grid 네임스페이스는 salesPlan 을 각각 놓친다.
    gridValueL10N 이 grid → translation 순으로 찾아주는 그 용도의 헬퍼다(예전 그리드 컬럼도 이걸 썼다).
  */
  const measureOptions = useMemo(
    () => Object.keys(dpcommonMeasureCd).map(cd => ({ key: cd, label: GridUtils.gridValueL10N(dpcommonMeasureCd[cd].name) })),
    // t 는 직접 쓰지 않지만, 언어를 바꿨을 때 라벨을 다시 풀려면 의존성에 있어야 한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dpcommonMeasureCd, t]
  );

  const selectRule = (key) => {
    setSelectedRuleKey(prev => ({ ...prev, [tab]: key }));
  }

  const patchRule = (key, patch) => {
    if (!key) {
      return;
    }
    const target = rules.find(rule => rule._key === key);
    if (!target) {
      return;
    }

    // 권한 서열(Owner ≥ Sub ≥ Others)은 저장 때가 아니라 고르는 순간 막는다.
    if (patch.groupPermission !== undefined && parseInt(patch.groupPermission, 10) > parseInt(target.ownerPermission, 10)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_ConfirmGroupPermission") });
      return;
    }
    if (patch.othersPermission !== undefined && parseInt(patch.othersPermission, 10) > parseInt(target.groupPermission, 10)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_ConfirmOthersPermission") });
      return;
    }

    const resolved = { ...patch };
    if (Object.prototype.hasOwnProperty.call(patch, "customerHrchyOption")) {
      const hrchy = customerHrchyData.find(item => item.hrchyCd === patch.customerHrchyOption?.key);
      delete resolved.customerHrchyOption;
      Object.assign(resolved, {
        customerHrchyCd: hrchy?.hrchyCd ?? null,
        customerHrchyName: hrchy?.name ?? null,
        customerHrchy: hrchy ?? null,
      });
    }
    if (Object.prototype.hasOwnProperty.call(patch, "itemHrchyOption")) {
      const hrchy = itemHrchyData.find(item => item.hrchyCd === patch.itemHrchyOption?.key);
      delete resolved.itemHrchyOption;
      Object.assign(resolved, {
        itemHrchyCd: hrchy?.hrchyCd ?? null,
        itemHrchyNm: hrchy?.name ?? null,
        itemHrchy: hrchy ?? null,
      });
    }
    if (Object.prototype.hasOwnProperty.call(patch, "measureOption")) {
      const commonCd = patch.measureOption?.key;
      const measure = commonCd ? dpcommonMeasureCd[commonCd] : null;
      delete resolved.measureOption;
      Object.assign(resolved, {
        measureCommonCd: commonCd ?? null,
        measureName: measure?.name ?? null,
        measureCd: measure?.value?.[0]?.cd ?? null,
        measure: measure ?? undefined,
      });
    }

    // Others 는 하위 직책에도 같이 내려간다(원래 그리드가 하던 전파). 상위에서 닫으면 아래도 닫힌다.
    const cascadeCds = resolved.othersPermission !== undefined
      ? new Set(postnClsrMap[target.postnCd] ?? [])
      : null;

    setRules(prev => prev.map(rule => {
      if (rule._key === key) {
        return { ...rule, ...resolved, _dirty: true };
      }
      if (cascadeCds && cascadeCds.has(rule.postnCd) && rule.othersPermission !== resolved.othersPermission) {
        return { ...rule, othersPermission: resolved.othersPermission, _dirty: true };
      }
      return rule;
    }));
  }

  const addRule = () => {
    if (!selectedPostn) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.validation, content: t("selectPostn") });
      return;
    }
    const key = newRuleKey();
    const base = {
      _key: key,
      _dirty: true,
      id: null,
      postnId: selectedPostn.id,
      postnCd: selectedPostn.postnCd,
      postnName: selectedPostn.name,
      postn: selectedPostn,
      verNum: 0,
    };
    const row = isMeasureTab
      ? { ...base, measureCd: null, measureName: null, measureCommonCd: null, measurePermsn: "644", ownerPermission: "6", groupPermission: "4", othersPermission: "4" }
      : {
        ...base, users: [],
        customerHrchyCd: topCustHrchy?.hrchyCd ?? null, customerHrchyName: topCustHrchy?.name ?? null, customerHrchy: null,
        itemHrchyCd: topItemHrchy?.hrchyCd ?? null, itemHrchyNm: topItemHrchy?.name ?? null, itemHrchy: null,
        nodePermsn: "600", ownerPermission: "6", groupPermission: "0", othersPermission: "0",
      };
    setRules(prev => [...prev, row]);
    selectRule(key);
  }

  const removeRule = () => {
    if (!activeRule) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }
    if (!activeRule.id) {
      setRules(prev => prev.filter(rule => rule._key !== activeRule._key));
      selectRule(null);
      return;
    }
    const ids = [activeRule.id];
    if (isMeasureTab && activeRule.pairRow) {
      ids.push(activeRule.pairRow.id);
    }
    const service = isMeasureTab ? measureAclService : hrchyAclService;
    service.remove(ids)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
      })
      .catch(e => {
        setSnackInfo({
          open: true,
          severity: e.response?.data ? snackBarMessageColor.unauthorized : snackBarMessageColor.saveFail,
          content: e.response?.data ? t(e.response.data) : t("MSG_DeleteFailed"),
        });
        debug(e);
      })
      .then(() => {
        selectRule(null);
        if (isMeasureTab) {
          retrieveMeasureAcl();
        } else {
          retrieveHrchyAcl();
        }
      });
  }

  const validatePermission = (row) => {
    if (row.ownerPermission === "0" && row.groupPermission === "0" && row.othersPermission === "0") {
      return "MSG_ConfirmPermission";
    }
    if (parseInt(row.groupPermission, 10) > parseInt(row.ownerPermission, 10)) {
      return "MSG_ConfirmGroupPermission";
    }
    if (parseInt(row.othersPermission, 10) > parseInt(row.groupPermission, 10)) {
      return "MSG_ConfirmOthersPermission";
    }
    return null;
  }

  const saveHrchyAcl = () => {
    const dirtyRows = hrchyRules.filter(rule => rule._dirty).map(rule => ({ ...rule }));
    if (isEmpty(dirtyRows)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    for (const row of dirtyRows) {
      const messageKey = validatePermission(row);
      if (messageKey) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t(messageKey) });
        return;
      }
      row.nodePermsn = parseInt(row.ownerPermission + row.groupPermission + row.othersPermission, 10);

      // 최상위 계층은 "제한 없음"이라 저장하지 않는다(서버가 null 을 전체 허용으로 읽는다).
      if (row.customerHrchyCd === topCustHrchy?.hrchyCd) {
        row.customerHrchy = null;
        row.customerHrchyCd = null;
        row.customerHrchyName = null;
      }
      if (row.itemHrchyCd === topItemHrchy?.hrchyCd) {
        row.itemHrchy = null;
        row.itemHrchyCd = null;
        row.itemHrchyNm = null;
      }
      delete row._key;
      delete row._dirty;
    }

    hrchyAclService.upsert(dirtyRows)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
      })
      .catch(e => {
        setSnackInfo({
          open: true,
          severity: e.response?.data ? snackBarMessageColor.unauthorized : snackBarMessageColor.saveFail,
          content: e.response?.data ? t(e.response.data) : t("MSG_SaveFailed"),
        });
        debug(e);
      })
      .then(() => {
        selectRule(null);
        retrieveHrchyAcl();
      });
  }

  const saveMeasureAcl = () => {
    const dirtyRows = measureRules.filter(rule => rule._dirty).map(rule => ({ ...rule }));
    if (isEmpty(dirtyRows)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    const saveRowData = [];
    for (const row of dirtyRows) {
      const messageKey = validatePermission(row);
      if (messageKey) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t(messageKey) });
        return;
      }
      if (!row.id && !row.measure) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.validation, content: t("MSG_NoSaveData") });
        return;
      }
      row.measurePermsn = parseInt(row.ownerPermission + row.groupPermission + row.othersPermission, 10);
      delete row._key;
      delete row._dirty;

      if (row.id === null) {
        row.measureCd = row.measure.value[0].cd;
        saveRowData.push(row);
        if (size(row.measure.value) > 1) {
          const newRowData = cloneDeep(row);
          newRowData.measureCd = newRowData.measure.value[1].cd;
          saveRowData.push(newRowData);
        }
      } else {
        const changeKeys = ["id", "verNum"];
        if (row.measure) {
          row.measureCd = row.measure.value[0].cd;
          if (size(row.measure.value) > 1) {
            const newRowData = cloneDeep(row);
            newRowData.measureCd = newRowData.measure.value[1].cd;
            if (row.pairRow) {
              for (const cKey of changeKeys) {
                newRowData[cKey] = row.pairRow[cKey];
              }
            } else {
              newRowData.id = null;
              newRowData.verNum = null;
            }
            saveRowData.push(newRowData);
          } else if (row.pairRow) {
            // 측정치를 금액/수량 쌍이 없는 것으로 바꿨다면 남은 AMT 짝을 지운다.
            measureAclService.remove([row.pairRow.id]);
          }
        } else if (row.pairRow) {
          const newRowData = cloneDeep(row);
          newRowData.measureCd = row.pairRow.measureCd;
          for (const cKey of changeKeys) {
            newRowData[cKey] = row.pairRow[cKey];
          }
          saveRowData.push(newRowData);
        }
        saveRowData.push(row);
      }
    }

    measureAclService.upsert(saveRowData)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
      })
      .catch(e => {
        setSnackInfo({
          open: true,
          severity: e.response?.data ? snackBarMessageColor.unauthorized : snackBarMessageColor.saveFail,
          content: e.response?.data ? t(e.response.data) : t("MSG_SaveFailed"),
        });
        debug(e);
      })
      .then(() => {
        selectRule(null);
        retrieveMeasureAcl();
      });
  }

  const handleRefresh = () => {
    selectRule(null);
    retrieveHrchyAcl();
    retrieveMeasureAcl();
    if (!isEmpty(userPostn)) {
      retrieveCustItemByPostnCd(userPostn);
    }
  }

  // ── 최종승인 지정/해제 (보드 우클릭) ────────────────────────────────────────
  const saveApproval = (postnList, kind) => {
    postnService.upsert(postnList)
      .then(response => {
        setPostnData(prev => prev.map(element => {
          const postnRow = response.data.find(item => item.postnCd === element.postnCd);
          if (!postnRow) {
            return element;
          }
          return {
            ...element,
            finalApvlFlg: kind === "assign" && postnRow.postnCd === apvlTarget,
            verNum: postnRow.verNum,
          };
        }));
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        setApvlTarget(null);
      });
  }

  const assignApproval = () => {
    if (!apvlTarget) {
      return;
    }
    const postn = postnData.filter(item => item.postnCd === apvlTarget).map(item => ({ ...item, finalApvlFlg: true }));
    postnService.getParentChildClrs({ postnCd: apvlTarget })
      .then(response => {
        const upsertData = (response.data ?? []).map(element => ({ ...element, finalApvlFlg: false }));
        saveApproval([...postn, ...upsertData], "assign");
      })
      .catch(e => {
        debug(e);
      });
  }

  const unassignApproval = () => {
    if (!apvlTarget) {
      return;
    }
    const postn = postnData.filter(item => item.postnCd === apvlTarget).map(item => ({ ...item, finalApvlFlg: false }));
    saveApproval(postn, "unassign");
  }

  const apvlActions = useMemo(() => {
    const target = apvlTarget ? postnByCd[apvlTarget] : null;
    if (!target) {
      return [];
    }
    return [
      { label: t("assignFinalApvl"), effect: assignApproval, apvlFlg: !!target.finalApvlFlg },
      { label: t("unassignFinalApvl"), effect: unassignApproval, apvlFlg: !target.finalApvlFlg },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apvlTarget, postnByCd, t]);

  // ── 접근 제어 on/off ────────────────────────────────────────────────────────
  const handleSwitchClick = (e, isPostn) => {
    e.preventDefault();
    const controlName = isPostn ? "postnAccessControl" : "approvalAccessControl";
    const currentState = isPostn ? toggled : approvalToggled;

    setDialogInfo({
      open: true, title: "confirm", actionName: isPostn ? "POSTN_SWITCH" : "APPROVAL_SWITCH",
      content: currentState ? t("MSG_ChangeAccessControlOff", { control: t(controlName) }) : t("MSG_ChangeAccessControlOn", { control: t(controlName) })
    });
  }

  const switchToggled = (e, isApproval = false) => {
    const configDto = [];
    let params = {};
    const accessControlData = isApproval ? approvalAccessControl : accessControl;
    if (Object.keys(accessControlData).length > 0) {
      params = { ...accessControlData }
      params.boolVal = e.target.checked;
    } else {
      params = {
        moduleCd: "DP",
        activeFlg: true,
        configCd: isApproval ? APPROVAL_ACCESS_CONTROL : ACCESS_CONTROL,
        entityName: "PlanConfig",
        boolVal: e.target.checked
      }
    }
    if (!isApproval) {
      if (params.boolVal) {
        const data = postnData.find(postn => postn.finalApvlFlg);
        if (!data) {
          setSnackInfo({
            open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_SetFinalAprl")
          });
          return;
        }
      }

      const approvalParam = size(approvalAccessControl) > 0 ? { ...approvalAccessControl, boolVal: e.target.checked } : {
        moduleCd: "DP",
        activeFlg: true,
        configCd: APPROVAL_ACCESS_CONTROL,
        entityName: "PlanConfig",
        boolVal: e.target.checked
      }
      configDto.push(approvalParam);
    }
    configDto.push(params)
    planConfigService.upsert([...configDto])
      .then(response => {
        if (response.data) {
          for (const resData of response?.data) {
            if (isEqual(resData?.configCd, APPROVAL_ACCESS_CONTROL)) {
              setApprovalAccessControl(resData);
            } else if (isEqual(resData?.configCd, ACCESS_CONTROL)) {
              setAccessControl(resData);
            }
          }
        }
      })
      .catch(error => {
        debug(error);
      })
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      if (isMeasureTab) {
        saveMeasureAcl();
      } else {
        saveHrchyAcl();
      }
    } else if (action === "DELETE") {
      removeRule();
    } else if (action === "POSTN_SWITCH") {
      switchToggled({ target: { checked: !toggled } }, false);
    } else if (action === "APPROVAL_SWITCH") {
      switchToggled({ target: { checked: !approvalToggled } }, true);
    }
  }

  // ── 선택 연동 ──────────────────────────────────────────────────────────────
  const handleSelectPostn = (postnCd) => {
    setSelectedPostnCd(postnCd);
    setSelectedRuleKey({ [dpAccessControlTab.CUSTHRCHY]: null, [dpAccessControlTab.MEASURE]: null });
  }

  useEffect(() => {
    // 규칙을 고르면 그 규칙이 걸린 계층의 고객/제품을, 아직 안 골랐으면 직책이 볼 수 있는 전체를 보여준다.
    if (!selectedPostnCd) {
      return;
    }
    if (activeRule && !isMeasureTab && (activeRule.customerHrchyCd || activeRule.itemHrchyCd)) {
      retrieveCustItemByHrchyCd({
        custHrchyCds: getLeafHrchyCds(activeRule.customerHrchyCd, customerHrchyData),
        itemHrchyCds: getLeafHrchyCds(activeRule.itemHrchyCd, itemHrchyData),
      });
    } else {
      retrieveCustItemByPostnCd([selectedPostnCd]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPostnCd, activeRuleKey, isMeasureTab]);

  useEffect(() => {
    if (isLoading) {
      retrieveAccessControl();
      retrieveUserInfo();
      retrievePostn();
      retrieveCustomerHrchy();
      retrieveItemHrchy();
      retrieveMeasureAcl();
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    if (!isEmpty(postnData)) {
      retrievePostnClsr();
    }
  }, [postnData]);

  useEffect(() => {
    // 최상위 계층 이름을 규칙 행에 채워 넣어야 해서, 두 계층이 다 온 뒤에 ACL 을 읽는다.
    if (!isEmpty(customerHrchyData) && !isEmpty(itemHrchyData)) {
      retrieveHrchyAcl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerHrchyData, itemHrchyData]);

  useEffect(() => {
    if (!selectedPostnCd && !isEmpty(postnData)) {
      setSelectedPostnCd(postnData[0].postnCd);
    }
  }, [postnData, selectedPostnCd]);

  /*
    두 스위치의 checked 는 반드시 boolean 이어야 한다. config 가 아직 안 왔을 때
    accessControl 은 빈 값이라 .boolVal 이 undefined 가 되는데, 그대로 넘기면
    MUI 가 controlled 스위치를 uncontrolled 로 바꿨다며 콘솔 에러를 낸다.
  */
  useEffect(() => {
    setToggled(!!accessControl?.boolVal);
  }, [accessControl]);

  useEffect(() => {
    if (isEmpty(approvalAccessControl) && accessControl?.boolVal) {
      setApprovalToggled(true);
    } else {
      setApprovalToggled(!!approvalAccessControl?.boolVal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalAccessControl, accessControl]);

  useEffect(() => {
    setIsLoading(true);
  }, []);

  // ── 구성원 목록 ────────────────────────────────────────────────────────────
  const memberSubtitle = () => {
    if (!activeRule) {
      // 규칙을 고르지 않았을 때는 부제를 비운다 — 제목만 남긴다.
      return "";
    }
    if (isMeasureTab) {
      return activeRule.measureName ? GridUtils.gridValueL10N(activeRule.measureName) : "—";
    }
    return [activeRule.customerHrchyName, activeRule.itemHrchyNm].filter(Boolean).join("  ·  ");
  }

  const board = (
    <PositionHierarchyBoard
      postns={postnData}
      ruleCountByPostn={ruleCountByPostn}
      selectedPostnCd={selectedPostnCd}
      onSelect={handleSelectPostn}
      apvlActions={apvlActions}
      onRequestApvlMenu={setApvlTarget}
      fullScreen={fullScreen}
      setFullScreen={setFullScreen}
    />
  );

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", height: 50, width: "100%" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 500, mr: 2, ml: "auto" }}>
          {t("postnAccessControl")}
        </Typography>
        <StyledSwitch size="small" sx={{ mr: 2 }} checked={toggled} onChange={(e) => handleSwitchClick(e, true)} />
        <Typography variant="subtitle2" sx={{ fontWeight: 500, mr: 2 }}>
          {t("approvalAccessControl")}
        </Typography>
        <StyledSwitch size="small" sx={{ mr: 2 }} disabled={!toggled} checked={approvalToggled} onChange={(e) => handleSwitchClick(e, false)} />
      </Box>

      {fullScreen ? (
        <AppCard sx={{ height: "calc(100% - 50px)", width: "100%", p: 1.5, display: "flex", flexDirection: "column" }}>
          {board}
        </AppCard>
      ) : (
        // wrap-flow-sx: 1280px 아래에서는 App.scss 가 좌/우를 세로로 쌓는다(패널 width 420 도 100% 로 덮인다).
        <Box className={"grid-area wrap-flow-sx"} sx={{ display: "flex", gap: 1.5, height: "100%", width: "100%", minWidth: 500 }}>
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
            {/*
              보드와 구성원 그리드는 6:4 로 나눈다. 고정 높이를 주면 화면이 커질수록 한쪽만 늘어난다 —
              flex 비율로 두면 어느 해상도에서도 같은 비율을 유지한다. minHeight 0 은 자식 스크롤이
              부모를 밀어내지 않게 하는 flex 관용구(없으면 그리드가 카드를 넘긴다).
            */}
            <AppCard sx={{ flex: 6, minHeight: 0, p: 1.5, display: "flex", flexDirection: "column" }}>
              {board}
            </AppCard>

            {/* 선택한 규칙이 실제로 어떤 고객·제품을 덮는지 — 계층 이름만으로는 확인이 안 되는 부분. */}
            <AppCard sx={{ flex: 4, minHeight: 0, display: "flex", flexDirection: "column" }}>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, px: 1.5, py: 1.25, flexShrink: 0 }}>
                <Box sx={CARD_HEADER_SX}>{t("ruleMembers")}</Box>
                <Box sx={{ fontSize: FS.SUB, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {memberSubtitle()}
                </Box>
              </Box>
              <Divider />
              <Box sx={{ flex: 1, minHeight: 0, display: "flex", gap: 1, p: 1 }}>
                <RuleMemberList label={t("customer")} rows={customerData} codeField="customerCd" nameField="customerNm" hrchyField="customerHrchyName" />
                <Divider orientation="vertical" flexItem />
                <RuleMemberList label={t("item")} rows={itemData} codeField="itemCd" nameField="itemNm" hrchyField="itemHrchyNm" />
              </Box>
            </AppCard>
          </Box>

          <AppCard sx={{ width: 420, flexShrink: 0, display: "flex", flexDirection: "column" }}>
            <PositionRulePanel
              postn={selectedPostn}
              postnPath={selectedPostnPath}
              ruleCount={visibleRules.length}
              tab={tab}
              onTabChange={setTab}
              rules={visibleRules}
              selectedRuleKey={activeRuleKey}
              onSelectRule={selectRule}
              onPatchRule={patchRule}
              customerHrchyOptions={customerHrchyOptions}
              itemHrchyOptions={itemHrchyOptions}
              measureOptions={measureOptions}
              dirtyCount={dirtyCount}
              onAdd={addRule}
              onRemove={() => setDialogInfo({ open: true, title: "delete", actionName: "DELETE", content: t("MSG_DeleteConfirm") })}
              onSave={() => setDialogInfo({ open: true, title: "save", actionName: "SAVE", content: t("MSG_SaveConfirm") })}
              onReset={handleRefresh}
            />
          </AppCard>
        </Box>
      )}

      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        actionName={dialogInfo.actionName}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  )
};


export default withTranslation()(AccessControlAdmin);
